import { useEffect, useState } from 'react';
import {
  Portal,
  Box,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import Footer from 'components/footer/FooterAdmin';
import Navbar from 'components/navbar/NavbarAdmin';
import Sidebar from 'components/sidebar/Sidebar';
import { useAuth } from 'contexts/AuthContext';
import { SidebarContext } from 'contexts/SidebarContext';
import AccessDenied from 'pages/AccessDenied';
import { Route, Routes, Navigate } from 'react-router-dom';
import routes from 'routes';

const ProtectedRoute = ({ element: Component, hasAccess, canCreate, canRead, canUpdate, canDelete }) => {
  if (!hasAccess) {
    return <AccessDenied />;
  }
  return (
    <Component
      canCreate={canCreate}
      canRead={canRead}
      canUpdate={canUpdate}
      canDelete={canDelete}
    />
  );
};

export default function Dashboard(props) {
  const { auth } = useAuth();
  const { ...rest } = props;
  const [fixed] = useState(false);
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const [mini, setMini] = useState(false);
  const [hovered, setHovered] = useState(false);

  const getRoute = () => {
    return window.location.pathname !== '/admin/full-screen-maps';
  };

  const getActiveRoute = (routes) => {
    let activeRoute = '';
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute = getActiveRoute(routes[i].items);
        if (collapseActiveRoute !== activeRoute) {
          return collapseActiveRoute;
        }
      } else {
        const routeRegex = new RegExp(`^${routes[i].layout}${routes[i].path.replace(':id', '[^/]+')}$`);
        if (routeRegex.test(window.location.pathname)) {
          return routes[i].name;
        }
      }
    }
    return activeRoute;
  };

  const getActiveNavbar = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbar(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].secondary;
        }
      }
    }
    return activeNavbar;
  };

  const getRoutes = (routes) => {
    return routes.map((route, key) => {
      if (route.layout === '/admin' && route.value) {
        let hasPermission = auth.user.is_super_admin
          ? auth.permissions?.map(permission => permission?.toLowerCase()).includes(route.value.toLowerCase())
          // : auth.permissions.map(permission => permission?.module?.toLowerCase()).includes(route.value.toLowerCase())
          : auth.permissions?.some(permission =>
            permission?.module?.toLowerCase() === route?.value?.toLowerCase() &&
            permission?.method?.toLowerCase() === route?.method?.toLowerCase()
          );

        let canCreate = true;
        let canRead = true;
        let canUpdate = true;
        let canDelete = true;

        if (!auth.user.is_super_admin) {
          canCreate = auth.permissions?.some(permission => permission?.module?.toLowerCase() === route.value?.toLowerCase() && permission?.method === 'create');
          canRead = auth.permissions?.some(permission => permission?.module?.toLowerCase() === route.value?.toLowerCase() && permission?.method === 'read');
          // hasPermission = canRead ? true : false;
          canUpdate = auth.permissions?.some(permission => permission?.module?.toLowerCase() === route.value?.toLowerCase() && permission?.method === 'update');
          canDelete = auth.permissions?.some(permission => permission?.module?.toLowerCase() === route.value?.toLowerCase() && permission?.method === 'delete');
        }

        return (
          <Route
            key={key}
            path={`${route.path}`}
            element={
              <ProtectedRoute
                element={route.component}
                hasAccess={hasPermission}
                canCreate={canCreate}
                canRead={canRead}
                canUpdate={canUpdate}
                canDelete={canDelete}
              />
            }
          />
        );
      }
      if (route.collapse) {
        return getRoutes(route.items);
      } else {
        return null;
      }
    });
  };

  document.documentElement.dir = 'ltr';
  const { onOpen } = useDisclosure();
  const bg = useColorModeValue('background.100', 'background.900');
  return (
    <Box bg={bg} h="100vh" w="100vw" overflow="hidden">
      <SidebarContext.Provider
        value={{
          toggleSidebar,
          setToggleSidebar,
        }}
      >
        <Sidebar
          hovered={hovered}
          setHovered={setHovered}
          mini={mini}
          routes={routes}
          display="none"
          {...rest}
        />
        <Box
          float="right"
          minHeight="100vh"
          height="100%"
          overflow="auto"
          position="relative"
          // maxHeight="100%"
          w={
            mini === false
              ? { base: '100%', xl: 'calc( 100% - 270px )' }
              : mini === true && hovered === true
                ? { base: '100%', xl: 'calc( 100% - 270px )' }
                : { base: '100%', xl: 'calc( 100% - 120px )' }
          }
          maxWidth={
            mini === false
              ? { base: '100%', xl: 'calc( 100% - 270px )' }
              : mini === true && hovered === true
                ? { base: '100%', xl: 'calc( 100% - 270px )' }
                : { base: '100%', xl: 'calc( 100% - 120px )' }
          }
          transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
          transitionDuration=".2s, .2s, .35s"
          transitionProperty="top, bottom, width"
          transitionTimingFunction="linear, linear, ease"
        >
          <Portal>
            <Box>
              <Navbar
                hovered={hovered}
                setMini={setMini}
                mini={mini}
                onOpen={onOpen}
                logoText={'FortFitness GYM'}
                brandText={getActiveRoute(routes)}
                secondary={getActiveNavbar(routes)}
                theme={props.theme}
                setTheme={props.setTheme}
                fixed={fixed}
                {...rest}
              />
            </Box>
          </Portal>

          {getRoute() ? (
            <Box
              mx="auto"
              p={{ base: '20px', md: '30px' }}
              pe="20px"
              // minH="100vh"
              pt="50px"
            >
              <Routes>
                {getRoutes(routes)}
                <Route
                  path="/"
                  element={<Navigate to="/admin/dashboards/default" replace />}
                />
              </Routes>
            </Box>
          ) : null}
          <Box
            width='100%'
            textAlign='center'
            position={"relative"}
            bottom={0}
          >
            <Footer />
          </Box>
        </Box>
      </SidebarContext.Provider>
    </Box>
  );
}
